import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const YEAR_PAGE    = 12; // years shown per page in year-grid view

// Inject slide keyframes once into the document head
let _stylesInjected = false;
function ensureStyles() {
  if (_stylesInjected || typeof document === 'undefined') return;
  _stylesInjected = true;
  const s = document.createElement('style');
  s.textContent = `
    @keyframes rh-mp-in-right {
      from { opacity: 0; transform: translateX(28px); }
      to   { opacity: 1; transform: translateX(0);    }
    }
    @keyframes rh-mp-in-left {
      from { opacity: 0; transform: translateX(-28px); }
      to   { opacity: 1; transform: translateX(0);     }
    }
    @keyframes rh-mp-in-up {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
  `;
  document.head.appendChild(s);
}

function parseYM(str) {
  if (!str) return null;
  const [y, m] = str.split('-').map(Number);
  return { year: y, month: m - 1 };
}

function formatYM(year, month) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function formatDisplay(str) {
  if (!str) return null;
  const p = parseYM(str);
  if (!p) return null;
  return `${MONTHS_LONG[p.month]} ${p.year}`;
}

// Returns the first year shown in a 12-year page that contains `year`
function pageStart(year) {
  return Math.floor(year / YEAR_PAGE) * YEAR_PAGE;
}

export default function RhMonthPicker({ value, onChange, placeholder = 'Pick month' }) {
  ensureStyles();

  const now   = new Date();
  const today = { year: now.getFullYear(), month: now.getMonth() };

  const parsed  = parseYM(value);
  const initial = parsed || today;

  // 'month' view or 'year' grid view
  const [viewMode, setViewMode]     = useState('month');
  const [viewYear, setViewYear]     = useState(initial.year);
  // First year shown in the year-grid page
  const [yearPage, setYearPage]     = useState(pageStart(initial.year));
  // Slide direction for animation: 'left' | 'right' | 'up'
  const [animDir, setAnimDir]       = useState('up');
  const [animKey, setAnimKey]       = useState(0);

  const [open, setOpen]             = useState(false);
  const [panelStyle, setPanelStyle] = useState({});

  const triggerRef = useRef(null);
  const panelRef   = useRef(null);

  // Sync view year when value changes externally
  useEffect(() => {
    if (parsed) {
      setViewYear(parsed.year);
      setYearPage(pageStart(parsed.year));
    }
  }, [value]); // eslint-disable-line

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current   && !panelRef.current.contains(e.target)
      ) setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  function openPanel() {
    const rect        = triggerRef.current.getBoundingClientRect();
    const panelWidth  = 248;
    const panelHeight = 230;
    const spaceBelow  = window.innerHeight - rect.bottom;
    const openUpward  = spaceBelow < panelHeight && rect.top > panelHeight;
    const wouldOverflowRight = rect.left + panelWidth > window.innerWidth - 12;
    setPanelStyle({
      position: 'fixed',
      zIndex: 9999,
      width: panelWidth,
      left: wouldOverflowRight ? rect.right - panelWidth : rect.left,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
    setViewMode('month');
    setOpen(true);
  }

  // ── Year navigation in month view ──────────────────────────────────────────

  function prevYear() {
    setAnimDir('right');
    setAnimKey(k => k + 1);
    setViewYear(y => y - 1);
  }

  function nextYear() {
    setAnimDir('left');
    setAnimKey(k => k + 1);
    setViewYear(y => y + 1);
  }

  // ── Year page navigation in year view ──────────────────────────────────────

  function prevYearPage() {
    setAnimDir('right');
    setAnimKey(k => k + 1);
    setYearPage(p => p - YEAR_PAGE);
  }

  function nextYearPage() {
    setAnimDir('left');
    setAnimKey(k => k + 1);
    setYearPage(p => p + YEAR_PAGE);
  }

  function openYearView() {
    setYearPage(pageStart(viewYear));
    setAnimDir('up');
    setAnimKey(k => k + 1);
    setViewMode('year');
  }

  function pickYear(y) {
    setViewYear(y);
    setAnimDir('up');
    setAnimKey(k => k + 1);
    setViewMode('month');
  }

  // ── Month / selection ──────────────────────────────────────────────────────

  function pickMonth(mIdx) {
    onChange(formatYM(viewYear, mIdx));
    setOpen(false);
  }

  function goThisMonth() {
    onChange(formatYM(today.year, today.month));
    setViewYear(today.year);
    setYearPage(pageStart(today.year));
    setOpen(false);
  }

  function clear() {
    onChange('');
    setOpen(false);
  }

  const isSelected  = (mIdx) => parsed && parsed.year === viewYear && parsed.month === mIdx;
  const isThisMonth = (mIdx) => today.year === viewYear && today.month === mIdx;

  const animStyle = {
    animation: animDir === 'left'
      ? `rh-mp-in-right 0.18s ease`
      : animDir === 'right'
      ? `rh-mp-in-left 0.18s ease`
      : `rh-mp-in-up 0.18s ease`,
  };

  return (
    <div ref={triggerRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* ── Trigger button ── */}
      <button
        onClick={() => (open ? setOpen(false) : openPanel())}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          border: `1px solid ${open || value ? 'var(--rh-orange)' : 'var(--rh-border)'}`,
          borderRadius: 8, padding: '5px 11px', fontSize: 13, fontWeight: 500,
          background: '#fff', cursor: 'pointer',
          color: value ? 'var(--rh-text)' : 'var(--rh-text-faint)',
          boxShadow: open ? '0 0 0 3px rgba(255,69,0,0.12)' : 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          whiteSpace: 'nowrap',
        }}
      >
        <svg width="13" height="13" fill="none" stroke={value ? 'var(--rh-orange)' : 'var(--rh-text-faint)'} strokeWidth="2" viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        {value ? formatDisplay(value) : placeholder}
        {value && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            title="Clear"
            style={{
              marginLeft: 2, lineHeight: 1,
              color: 'var(--rh-text-faint)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center',
            }}
          >
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </span>
        )}
      </button>

      {/* ── Panel ── */}
      {open && createPortal(
        <div ref={panelRef} style={{
          ...panelStyle,
          background: '#fff', border: '1px solid var(--rh-border)',
          borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          padding: '14px 14px 10px', userSelect: 'none',
          overflow: 'hidden',
        }}>

          {viewMode === 'month' ? (
            <>
              {/* Month-view header: ‹  2026  › */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <button onClick={prevYear} style={navBtnStyle}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                {/* Clicking the year label opens the year grid */}
                <button
                  onClick={openYearView}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 14, fontWeight: 700, color: 'var(--rh-text)',
                    padding: '2px 8px', borderRadius: 6,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                  title="Choose year"
                >
                  {viewYear}
                  <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                <button onClick={nextYear} style={navBtnStyle}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>

              {/* Animated 4×3 month grid */}
              <div key={animKey} style={{ ...animStyle, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                {MONTHS_SHORT.map((name, mIdx) => {
                  const sel  = isSelected(mIdx);
                  const curr = isThisMonth(mIdx);
                  return (
                    <button
                      key={mIdx}
                      onClick={() => pickMonth(mIdx)}
                      style={{
                        padding: '7px 4px', fontSize: 13, fontWeight: sel || curr ? 700 : 400,
                        borderRadius: 7, cursor: 'pointer',
                        border: curr && !sel ? '1.5px solid var(--rh-orange)' : '1.5px solid transparent',
                        background: sel ? 'var(--rh-orange)' : curr ? 'rgba(255,69,0,0.08)' : 'transparent',
                        color: sel ? '#fff' : curr ? 'var(--rh-orange)' : 'var(--rh-text)',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => {
                        if (!sel) e.currentTarget.style.background = curr ? 'rgba(255,69,0,0.18)' : 'rgba(0,0,0,0.05)';
                      }}
                      onMouseLeave={e => {
                        if (!sel) e.currentTarget.style.background = curr ? 'rgba(255,69,0,0.08)' : 'transparent';
                      }}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {/* Year-grid header: ‹  2020 – 2031  › */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <button onClick={prevYearPage} style={navBtnStyle}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--rh-text-muted)' }}>
                  {yearPage} – {yearPage + YEAR_PAGE - 1}
                </span>
                <button onClick={nextYearPage} style={navBtnStyle}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>

              {/* Animated 4×3 year grid */}
              <div key={animKey} style={{ ...animStyle, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                {Array.from({ length: YEAR_PAGE }, (_, i) => yearPage + i).map((y) => {
                  const isCurr = y === today.year;
                  const isSel  = y === viewYear;
                  return (
                    <button
                      key={y}
                      onClick={() => pickYear(y)}
                      style={{
                        padding: '7px 2px', fontSize: 13, fontWeight: isSel || isCurr ? 700 : 400,
                        borderRadius: 7, cursor: 'pointer',
                        border: isCurr && !isSel ? '1.5px solid var(--rh-orange)' : '1.5px solid transparent',
                        background: isSel ? 'var(--rh-orange)' : isCurr ? 'rgba(255,69,0,0.08)' : 'transparent',
                        color: isSel ? '#fff' : isCurr ? 'var(--rh-orange)' : 'var(--rh-text)',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => {
                        if (!isSel) e.currentTarget.style.background = isCurr ? 'rgba(255,69,0,0.18)' : 'rgba(0,0,0,0.05)';
                      }}
                      onMouseLeave={e => {
                        if (!isSel) e.currentTarget.style.background = isCurr ? 'rgba(255,69,0,0.08)' : 'transparent';
                      }}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Footer */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            marginTop: 10, paddingTop: 10,
            borderTop: '1px solid var(--rh-border)',
          }}>
            <button onClick={clear} style={footerBtnStyle('#9ca3af')}>Clear</button>
            {viewMode === 'year'
              ? <button onClick={() => { setAnimDir('up'); setAnimKey(k => k + 1); setViewMode('month'); }} style={footerBtnStyle('var(--rh-text-muted)')}>← Back</button>
              : <button onClick={goThisMonth} style={footerBtnStyle('var(--rh-orange)')}>This month</button>
            }
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

const navBtnStyle = {
  background: 'none', border: '1px solid var(--rh-border)', borderRadius: 7,
  width: 28, height: 28, cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center', color: 'var(--rh-text)',
  padding: 0, transition: 'background 0.1s',
};

function footerBtnStyle(color) {
  return {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, color, padding: '2px 6px', borderRadius: 6,
  };
}
